import React from 'react'
import { DateTime } from 'luxon'
import { faCalendar } from '@fortawesome/pro-solid-svg-icons'

import { Badge } from '@/ui'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'

import type { ReportStatus, Urgency } from '../lib/types'

/** "Sep 15", or "Jan 1, 2026" once the date rolls into a year past the current one. */
const formatShortDue = (iso: string): string => {
  const due = isoToUTCDateTime(iso)
  if (!due.isValid) return iso
  return due.toFormat(
    due.year > DateTime.now().year
      ? DATE_FORMATS_LUXON.DISPLAY_SHORT_NO_PAD
      : DATE_FORMATS_LUXON.MONTH_DAY
  )
}

const URGENCY_COLOR = {
  expired: 'orange',
  due_soon: 'orange',
  upcoming: 'olive',
} as const satisfies Record<Urgency, string>

/**
 * One badge for "when is this due" — color follows urgency (orange = overdue
 * or due soon, olive = upcoming) only when the obligation both needs attention
 * and sits in a high-stakes area; everything else (covered, not-relevant, or
 * low/minor-stakes areas) stays a plain white badge, since there's nothing
 * urgent worth signaling with color.
 *
 * Overdue and due-soon share the orange: the two are separated by the prefix
 * ("Overdue" vs "Due"), not by hue, so nothing rests on telling two warm
 * colors apart.
 */
export const UrgencyBadge: React.FC<{
  status: ReportStatus
  urgency: Urgency | null
  dueAt: string | null
  highStakes: boolean
  /**
   * How many instances behind this badge are overdue. Above one, the badge
   * counts them instead of naming a date: a collapsed row shows the soonest
   * instance's date, which reads as *the* deadline when several states have
   * their own. Each state's own date is on its block below.
   */
  overdueCount?: number
  /**
   * Names the installment a recurring obligation is on ("Q3"). Preferred over
   * the date on a collapsed row: the row stands for a schedule, so a single
   * date reads as the only deadline. The dates themselves are on the rows
   * inside the card.
   */
  periodLabel?: string | null
}> = ({
  status,
  urgency,
  dueAt,
  highStakes,
  overdueCount = 0,
  periodLabel = null,
}) => {
  if (!dueAt) return null
  const isNeutral = status === 'covered' || status === 'not_relevant'
  const color =
    !isNeutral && highStakes && urgency ? URGENCY_COLOR[urgency] : 'white'
  const prefix = !isNeutral && urgency === 'expired' ? 'Overdue' : 'Due'
  const periodPhrase =
    urgency === 'expired'
      ? 'overdue'
      : urgency === 'due_soon'
        ? 'coming soon'
        : 'up next'
  const label =
    overdueCount > 1
      ? `${overdueCount} overdue`
      : periodLabel
        ? `${periodLabel} ${periodPhrase}`
        : `${prefix} ${formatShortDue(dueAt)}`
  return (
    <Badge variant="secondary" color={color} size="sm" icon={faCalendar}>
      {label}
    </Badge>
  )
}
